import { isNative } from "./platform";

/**
 * One-time native chrome setup: theme the status bar, hide the splash once the
 * web app is interactive, and route the Android hardware back button through
 * the SPA (double-back-to-exit at the root). No-ops on web.
 *
 * `navigateBack` returns true if the app handled the back press (had in-app
 * history to pop), false to fall through to the exit-confirm behaviour.
 */
export function initNativeShell(navigateBack: () => boolean): void {
  if (!isNative()) return;

  void (async () => {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    await StatusBar.setBackgroundColor({ color: "#1a5b3e" }).catch(() => {});

    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide().catch(() => {});

    const { App } = await import("@capacitor/app");
    let lastBack = 0;
    void App.addListener("backButton", () => {
      const handled = navigateBack();
      if (handled) return;
      const now = Date.now();
      if (now - lastBack < 1500) {
        void App.exitApp();
      } else {
        lastBack = now;
        console.log("Press back again to exit");
      }
    });
  })();
}

/** Light haptic tap on key actions (save/delete confirm). No-op on web. */
export async function tapHaptic(): Promise<void> {
  if (!isNative()) return;
  const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
  await Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
}
